import { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";

export default function ProductModal({ product, onClose, openChatWithUser, currentUser }) {
  const [currentImage, setCurrentImage] = useState(0);
  const [loading, setLoading] = useState(false);
  const isHandlingRef = useRef(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Para animación de entrada
    setShow(true);
  }, []);

  if (!product) return null;

  const images = product.media || [];
  const vendorId = product.userId || product.vendorId || product.uid || null;
  const isSold = product.status === "Vendido";
  const isOwnProduct = currentUser?.uid && vendorId && currentUser.uid === vendorId;
  const isPending = currentUser?.status === "pending";

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const comprarMensaje = `Hola, estoy interesado en adquirir tu producto "${product.title}". ¿Podemos coordinar los detalles? Gracias.`;

  const handleOpenChat = async (message = "") => {
    if (!vendorId) return Swal.fire({ toast: true, position: "top-end", icon: "warning", title: "Vendedor no disponible.", showConfirmButton: false, timer: 3000 });
    if (isHandlingRef.current) return;
    isHandlingRef.current = true;
    setLoading(true);

    try {
      if (typeof openChatWithUser === "function") {
        await openChatWithUser(vendorId, message);
      } else {
        Swal.fire({ toast: true, position: "top-end", icon: "error", title: "Función de chat no disponible.", showConfirmButton: false, timer: 3000 });
      }
    } finally {
      setLoading(false);
      isHandlingRef.current = false;
    }
  };

  // Cerrar con animación
  const handleClose = () => {
    setShow(false);
    setTimeout(onClose, 300);
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 transition-opacity duration-300 ${
        show ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      aria-modal="true"
      role="dialog"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      onClick={handleClose}
    >
      <div
        className={`bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 relative
        transform transition-transform duration-300 ${
          show ? "scale-100" : "scale-95"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white font-bold text-3xl leading-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
          aria-label="Cerrar modal"
          disabled={loading}
          type="button"
        >
          &times;
        </button>

        <h2
          id="modal-title"
          className="text-3xl font-extrabold mb-6 text-gray-900 dark:text-white"
        >
          {product.title}
        </h2>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Imagen y controles */}
          <div className="md:w-1/2 relative">
            {images.length > 0 ? (
              <>
                <img
                  src={images[currentImage]}
                  alt={`${product.title} - imagen ${currentImage + 1}`}
                  className="w-full h-72 md:h-80 object-contain rounded-lg shadow-md"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-gray-300 bg-opacity-60 hover:bg-opacity-90 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full p-2 shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Imagen anterior"
                      disabled={loading}
                      type="button"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-gray-700 dark:text-gray-200"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-gray-300 bg-opacity-60 hover:bg-opacity-90 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full p-2 shadow-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                      aria-label="Imagen siguiente"
                      disabled={loading}
                      type="button"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-gray-700 dark:text-gray-200"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
                {/* Indicadores de imagen */}
                <div className="flex justify-center mt-3 space-x-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      className={`w-3 h-3 rounded-full transition-colors ${
                        idx === currentImage ? "bg-blue-600" : "bg-gray-400 dark:bg-gray-600"
                      }`}
                      aria-label={`Ir a la imagen ${idx + 1}`}
                      onClick={() => setCurrentImage(idx)}
                      type="button"
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400">No hay imágenes disponibles.</p>
            )}
          </div>

          {/* Información del producto */}
          <div className="md:w-1/2 flex flex-col justify-between">
            <div>
              <p
                id="modal-description"
                className="mb-4 text-gray-700 dark:text-gray-300 text-base leading-relaxed"
              >
                {product.description}
              </p>

              <p className="font-extrabold text-2xl text-blue-600 mb-3">
                RD$ {product.price}
              </p>
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                Categoría: <span className="font-semibold">{product.category}</span>
              </p>

              <div className="mb-6 space-y-1 text-gray-700 dark:text-gray-300 text-sm">
                <p>
                  <span className="font-semibold">Condición:</span> {product.condition || "No especificado"}
                </p>
                <p>
                  <span className="font-semibold">Tipo de entrega:</span> {product.delivery || "No especificado"}
                </p>
                <p>
                  <span className="font-semibold">Garantía:</span> {product.warranty || "No"}
                </p>
                <p>
                  <span className="font-semibold">Métodos de pago:</span>
                </p>
                {product.paymentMethods && product.paymentMethods.length > 0 ? (
                  <ul className="list-disc list-inside ml-5">
                    {product.paymentMethods.map((method, idx) => (
                      <li key={idx}>{method}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No especificados</p>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              {isOwnProduct ? (
                <div className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-5 py-3 rounded-lg text-center text-sm font-medium">
                  📦 Esta es tu publicación
                </div>
              ) : isPending ? (
                <div className="flex-1 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300 px-5 py-3 rounded-lg text-center text-sm font-medium">
                  ⏳ Verifica tu cuenta para contactar al vendedor
                </div>
              ) : (
                <>
                  <button
                    className="flex-1 bg-green-600 text-white px-5 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-green-400 focus:ring-opacity-75"
                    onClick={() => handleOpenChat()}
                    disabled={loading}
                    type="button"
                  >
                    Contactar
                  </button>

                  <button
                    className="flex-1 bg-blue-600 text-white px-5 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-blue-400 focus:ring-opacity-75"
                    onClick={() => handleOpenChat(comprarMensaje)}
                    disabled={loading || isSold}
                    title={isSold ? "Producto vendido" : ""}
                    type="button"
                  >
                    Comprar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
